import { Divider, Radio, Space } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";

interface Option {
  label: string;
  value: string;
}

interface Props {
  props: FilterDropdownProps;
  options: Option[];
}

/**
 * Replacement for AntD Table's default column-filter dropdown.
 *
 * AntD's default needs three clicks: icon → pick option → OK. The
 * operator wanted two: icon → option-applies-on-click. We also surface
 * "Show all" as an explicit reset row under a divider so clearing the
 * filter is itself one click rather than reopening the dropdown and
 * fishing for the Reset button.
 */
export const QuickFilterDropdown = ({ props, options }: Props) => {
  const { setSelectedKeys, selectedKeys, confirm, clearFilters } = props;
  const current = (selectedKeys[0] as string | undefined) ?? null;

  const pick = (value: string) => {
    setSelectedKeys([value]);
    // confirm() closes the dropdown + commits the filter, which fires
    // onChange on the Table so the page's URL-sync runs.
    confirm({ closeDropdown: true });
  };
  const reset = () => {
    clearFilters?.();
    confirm({ closeDropdown: true });
  };

  return (
    <div style={{ padding: 8, minWidth: 160 }}>
      <Radio.Group
        value={current}
        onChange={(e) => pick(e.target.value as string)}
      >
        <Space direction="vertical">
          {options.map((o) => (
            <Radio key={o.value} value={o.value}>
              {o.label}
            </Radio>
          ))}
        </Space>
      </Radio.Group>
      <Divider style={{ margin: "8px 0" }} />
      <Radio
        checked={current === null}
        onClick={reset}
        // Radio + onClick is the cheap "single-click reset" we want —
        // checked is driven by the absence of a selection, and clicking
        // either the dot or the label clears.
      >
        Show all
      </Radio>
    </div>
  );
};
